import { useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useRouteParams } from '@/contexts/RouteContext';
import type { VehicleViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { VehicleForm } from './vehicle-form';
import {
	buildUpdateVehicleInput,
	mapVehicleToFormData,
	type VehicleFormSubmitData
} from './vehicle-input';
import {
	VEHICLE_QUERY,
	VEHICLE_TYPES_QUERY,
	VEHICLE_UPDATE_MUTATION
} from './vehicles.gql.tsx';

type VehicleTypeOption = {
	vehicleTypeId: number;
	name?: string | null;
};

type VehicleRecord = VehicleViewModel;

const isValidUlid = (value: string | undefined): value is string => {
	if (!value) {
		return false;
	}

	if (value.length !== 26) {
		return false;
	}

	return /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/i.test(value);
};

export const VehiclesEdit = () => {
	const { t } = useTranslation();
	const go = useGo();
	const { id } = useRouteParams();
	useEditPageHeader({ id, resourceName: 'vehicles' });

	const {
		data: vehicleResponse,
		isLoading: isVehicleLoading,
		isError: vehicleError,
		error: fetchError
	} = useOne<VehicleRecord>({
		resource: 'vehicles',
		id: id ?? '',
		meta: {
			gqlQuery: VEHICLE_QUERY,
			dataPath: 'vehicle'
		},
		queryOptions: {
			enabled: Boolean(id)
		}
	});

	const vehicle = vehicleResponse?.data;

	const {
		data: vehicleTypesData,
		loading: isVehicleTypeLoading,
		error: vehicleTypesError
	} = useQuery<{ vehicleTypes: VehicleTypeOption[] }>(VEHICLE_TYPES_QUERY);

	const vehicleTypeOptions = useMemo(
		() =>
			(vehicleTypesData?.vehicleTypes ?? [])
				.filter(type => type?.vehicleTypeId != null)
				.map(type => ({
					value: String(type.vehicleTypeId),
					label: type.name ?? `Type ${type.vehicleTypeId}`
				})),
		[vehicleTypesData]
	);

	const [updateVehicle, { loading: isSubmitting }] = useMutation(
		VEHICLE_UPDATE_MUTATION
	);

	// Memoize initial form data to prevent unnecessary form resets
	// Must be called before any conditional returns (Rules of Hooks)
	const initialFormData = useMemo(
		() => (vehicle ? mapVehicleToFormData(vehicle) : undefined),
		[vehicle]
	);

	const handleSubmit = async (formData: VehicleFormSubmitData) => {
		if (!vehicle?.vehicleId) {
			showMutationErrorToast('vehicles', 'update', { message: 'Vehicle identifier is missing.' });
			return;
		}

		const payload = buildUpdateVehicleInput(formData, vehicle.vehicleId);

		try {
			const { data } = await updateVehicle(
				getMutationOptions('vehicles', { input: payload })
			);

			const message = extractCheckResultError(data?.updateVehicle);

			if (message) {
				showMutationErrorToast('vehicles', 'update', { message });
				return;
			}

			showMutationSuccessToast('vehicles', 'update');
			go({
				to: '/vehicles',
				state: { selectVehicleId: vehicle.vehicleId }
			});
		} catch (error) {
			showMutationErrorToast('vehicles', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Vehicle not found</AlertTitle>
					<AlertDescription>
						A vehicle identifier was not provided in the URL.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (!isValidUlid(id)) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Invalid vehicle ID</AlertTitle>
					<AlertDescription>
						The provided identifier "{id}" is not a valid ULID.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (isVehicleLoading) {
		return (
			<FormPageLayout>
				<div className="space-y-4 text-sm text-muted-foreground">
					Loading vehicle details...
				</div>
			</FormPageLayout>
		);
	}

	if (vehicleError || !vehicle) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Unable to load vehicle</AlertTitle>
					<AlertDescription>
						{fetchError?.message ??
							'The requested vehicle could not be found.'}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<FormPageLayout>
			{vehicleTypesError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load vehicle types</AlertTitle>
					<AlertDescription>
						{vehicleTypesError.message}
					</AlertDescription>
				</Alert>
			)}

			<VehicleForm
				key={vehicle.vehicleId}
				mode="edit"
				initialData={initialFormData}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				vehicleTypeOptions={vehicleTypeOptions}
				isVehicleTypeLoading={isVehicleTypeLoading}
				headerTitle={t('vehicles:edit.title')}
			/>
		</FormPageLayout>
	);
};
